import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShieldHalved, faUpload, faTimesCircle,
  faSpinner, faUserPlus, faUsers, faTrash, faPlus, faTimes,
  faWandMagicSparkles, faRobot, faCheckCircle, faExclamationTriangle,
  faDownload, faChevronDown, faChevronUp, faBell,
} from "@fortawesome/free-solid-svg-icons";
import { Container } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../api/axiosInstance";
import { API_ENDPOINTS } from "../../api/config";
import insuranceService, {
  InsurancePolicyPayload,
  InsuranceChoices,
  InsuranceCompanyOption,
  InsurancePolicyDocument,
} from "../../services/insuranceService";
import { API_BASE_URL } from "../../api/config";
import TypeSpecificSection from "./TypeSpecificFields";
import { useDocumentExtraction } from "../../hooks/useDocumentExtraction";
import "./InsuranceRecord.css";
import BackButton from '../../components/BackButton/BackButton';
import ConfirmDialog from '../../components/ConfirmDialog/ConfirmDialog';
import { useAuth } from '../../context/AuthContext';

interface DependantOption {
  id: number;
  name: string;
  relationship: number | { id: number; name: string } | string;
  gender?: string;
}

interface RelationshipType {
  id: number;
  name: string;
}

interface FloaterMemberEntry {
  dependant_id: number;
  name: string;
  relationship: string;
  uhid: string;
}

// Insurance types that support Individual / Floater plan choice
const FLOATER_ELIGIBLE_TYPES = new Set(["health", "personal_accident", "critical_illness"]);

// Insurance types where "Company" owner option is relevant (group/employer policies)
const COMPANY_OWNER_TYPES = new Set(["health", "life", "personal_accident", "critical_illness"]);

function getPolicyHolderPreview(
  form: { policy_owner_type: string; plan_type: string; dependant: number | null },
  dependants: { id: number; name: string }[],
  currentUserName: string,
): string {
  if (form.plan_type === "floater") return currentUserName;
  if (form.policy_owner_type === "dependant") {
    const dep = dependants.find((d) => d.id === form.dependant);
    return dep ? dep.name : "— pick a family member —";
  }
  // self or company
  return currentUserName;
}

const InsuranceRecord: React.FC = () => {
  const { t } = useTranslation(['insurance', 'common']);
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const { user } = useAuth();
  const currentUserName = user?.name || user?.username || 'You';

  const [saving, setSaving] = useState(false);

  // In-app toast notification (replaces browser alert)
  const [toastMsg, setToastMsg] = useState<{ message: string; type: 'success' | 'error' | 'warning' } | null>(null);
  const showToast = (message: string, type: 'success' | 'error' | 'warning' = 'error') => {
    setToastMsg({ message, type });
    setTimeout(() => setToastMsg(null), 4000);
  };

  // Confirm dialog (replaces window.confirm)
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; title: string; message: string; onConfirm: () => void }>({ open: false, title: '', message: '', onConfirm: () => {} });
  const [loadingChoices, setLoadingChoices] = useState(true);
  const [choices, setChoices] = useState<InsuranceChoices | null>(null);
  const [documents, setDocuments] = useState<File[]>([]);
  const [existingDocuments, setExistingDocuments] = useState<InsurancePolicyDocument[]>([]);

  // AI extraction
  const {
    step: extractionStep,
    progress: extractionProgress,
    error: extractionError,
    extractedData,
    extractFromFile,
    reset: resetExtraction,
  } = useDocumentExtraction();
  const [autoFilledFields, setAutoFilledFields] = useState<Set<string>>(new Set());
  const [scannerExpanded, setScannerExpanded] = useState(true);

  // Company dropdown state
  const [companies, setCompanies] = useState<InsuranceCompanyOption[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [isOtherCompany, setIsOtherCompany] = useState(false);
  const [customCompanyName, setCustomCompanyName] = useState("");

  // Type-specific data
  const [typeSpecificData, setTypeSpecificData] = useState<Record<string, any>>({});

  // Floater members state
  const [dependants, setDependants] = useState<DependantOption[]>([]);
  const [relationshipTypes, setRelationshipTypes] = useState<RelationshipType[]>([]);
  const [loadingDependants, setLoadingDependants] = useState(false);
  const [floaterMembers, setFloaterMembers] = useState<FloaterMemberEntry[]>([]);

  // Add new member inline form
  const [showAddForm, setShowAddForm] = useState(false);
  const [addingMember, setAddingMember] = useState(false);
  const [newMember, setNewMember] = useState({
    name: "",
    relationship: "",
    gender: "",
    dob: "",
    mobile_number: "",
    email: "",
  });

  const [form, setForm] = useState({
    policy_owner_type: "self" as "self" | "dependant" | "company",
    plan_type: "individual" as "individual" | "floater",
    dependant: null as number | null,
    policy_from: "",
    policy_to: "",
    type_of_insurance: "health",
    insurance_company: "",
    policy_number: "",
    policy_name: "",
    sum_assured: "",
    premium_amount: "",
    tpa: "",
    nominee: "",
    notes: "",
    group_policy_details: "",
    policy_features: "",
    renewal_reminder_enabled: false,
    renewal_frequency: "one_time" as string,
    renewal_reminder_type: "months" as string,
    renewal_reminder_value: "1",
  });

  // Load choices on mount
  useEffect(() => {
    const loadChoices = async () => {
      try {
        const data = await insuranceService.getChoices();
        setChoices(data);
      } catch (err) {
        console.error("Failed to load choices:", err);
      } finally {
        setLoadingChoices(false);
      }
    };
    loadChoices();
  }, []);

  // Fetch companies when type_of_insurance changes
  const fetchCompanies = useCallback(async (type: string) => {
    setLoadingCompanies(true);
    try {
      const data = await insuranceService.getCompanies(type);
      setCompanies(data);
    } catch (err) {
      console.error("Failed to load companies:", err);
      setCompanies([]);
    } finally {
      setLoadingCompanies(false);
    }
  }, []);

  useEffect(() => {
    fetchCompanies(form.type_of_insurance);
  }, [form.type_of_insurance, fetchCompanies]);

  useEffect(() => {
    const needsDependants =
      form.plan_type === "floater" || form.policy_owner_type === "dependant";
    if (!needsDependants) return;
    const fetchData = async () => {
      setLoadingDependants(true);
      try {
        const [depRes, relRes] = await Promise.all([
          axiosInstance.get(API_ENDPOINTS.dependants.list),
          axiosInstance.get("/api/dependants/relationship-types/"),
        ]);
        const depData = Array.isArray(depRes.data) ? depRes.data : depRes.data.results || depRes.data.data || [];
        setDependants(depData);
        const relData = Array.isArray(relRes.data) ? relRes.data : relRes.data.results || relRes.data.data || [];
        setRelationshipTypes(relData);
      } catch (err) {
        console.error("Failed to load dependants:", err);
      } finally {
        setLoadingDependants(false);
      }
    };
    fetchData();
  }, [form.plan_type, form.policy_owner_type]);

  useEffect(() => {
    if (form.policy_owner_type !== "dependant" && form.dependant !== null) {
      setForm((prev) => ({ ...prev, dependant: null }));
    }
  }, [form.policy_owner_type, form.dependant]);

  // If editing, load existing policy
  useEffect(() => {
    if (!id) return;
    const loadPolicy = async () => {
      try {
        const policy = await insuranceService.getPolicy(Number(id));
        setForm({
          policy_owner_type: policy.policy_owner_type,
          plan_type: policy.plan_type,
          dependant: policy.dependant ?? null,
          policy_from: policy.policy_from,
          policy_to: policy.policy_to,
          type_of_insurance: policy.type_of_insurance,
          insurance_company: policy.insurance_company,
          policy_number: policy.policy_number,
          policy_name: policy.policy_name || "",
          sum_assured: policy.sum_assured?.toString() || "",
          premium_amount: policy.premium_amount?.toString() || "",
          tpa: policy.tpa || "",
          nominee: policy.nominee || "",
          notes: policy.notes || "",
          group_policy_details: policy.group_policy_details || "",
          policy_features: policy.policy_features || "",
          renewal_reminder_enabled: policy.renewal_reminder_enabled || false,
          renewal_frequency: policy.renewal_frequency || "one_time",
          renewal_reminder_type: policy.renewal_reminder_type || "months",
          renewal_reminder_value: String(policy.renewal_reminder_value || 1),
        });

        // Load type-specific data
        if (policy.type_specific_data && Object.keys(policy.type_specific_data).length > 0) {
          setTypeSpecificData(policy.type_specific_data);
        }

        // Load existing documents
        if (policy.documents && policy.documents.length > 0) {
          setExistingDocuments(policy.documents);
        }

        // Load existing floater members
        if (policy.plan_type === "floater" && policy.floater_members?.length) {
          setFloaterMembers(
            policy.floater_members
              .filter((m) => !m.is_self && m.dependant_data)
              .map((m) => ({
                dependant_id: m.dependant_data!.id,
                name: m.dependant_data!.name,
                relationship: m.dependant_data!.relationship || "",
                uhid: m.uhid || "",
              }))
          );
        }

        // After companies load, check if the company is "Other"
        const companiesForType = await insuranceService.getCompanies(policy.type_of_insurance);
        setCompanies(companiesForType);
        const isKnown = companiesForType.some((c) => c.name === policy.insurance_company);
        if (!isKnown && policy.insurance_company) {
          setIsOtherCompany(true);
          setCustomCompanyName(policy.insurance_company);
        }
      } catch (err) {
        console.error("Failed to load policy:", err);
      }
    };
    loadPolicy();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleInsuranceTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    const resetPlan = !FLOATER_ELIGIBLE_TYPES.has(value);
    const resetOwner = !COMPANY_OWNER_TYPES.has(value);
    setForm((prev) => ({
      ...prev,
      type_of_insurance: value,
      insurance_company: "",
      ...(resetPlan ? { plan_type: "individual" as const } : {}),
      ...(resetOwner && prev.policy_owner_type === "company" ? { policy_owner_type: "self" as const } : {}),
    }));
    if (resetPlan) setFloaterMembers([]);
    setIsOtherCompany(false);
    setCustomCompanyName("");
    setTypeSpecificData({});
  };

  const handleCompanyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value === "__other__") {
      setIsOtherCompany(true);
      setForm((prev) => ({ ...prev, insurance_company: "" }));
    } else {
      setIsOtherCompany(false);
      setCustomCompanyName("");
      setForm((prev) => ({ ...prev, insurance_company: value }));
    }
  };

  const handleCustomCompanyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCustomCompanyName(value);
    setForm((prev) => ({ ...prev, insurance_company: value }));
  };

  // AI extraction handlers
  const handleExtractionUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validTypes = ["application/pdf", "image/jpeg", "image/png", "image/jpg"];
    if (!validTypes.includes(file.type)) {
      showToast(t('insurance:messages.uploadPdfOrImage'), 'warning');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      showToast(t('insurance:messages.fileSizeLimit'), 'warning');
      return;
    }
    await extractFromFile(file);
    // Also attach the scanned file as a policy document
    setDocuments((prev) => {
      // Avoid duplicates if user re-uploads
      if (prev.some((f) => f.name === file.name && f.size === file.size)) return prev;
      return [...prev, file];
    });
  };

  // Auto-fill form when AI extraction completes
  useEffect(() => {
    if (!extractedData) return;

    const filled = new Set<string>();

    setForm((prev) => {
      const updated = { ...prev };
      if (extractedData.type_of_insurance) {
        updated.type_of_insurance = extractedData.type_of_insurance;
        filled.add("type_of_insurance");
      }
      if (extractedData.insurance_company) {
        updated.insurance_company = extractedData.insurance_company;
        filled.add("insurance_company");
      }
      if (extractedData.policy_number) {
        updated.policy_number = extractedData.policy_number;
        filled.add("policy_number");
      }
      if (extractedData.policy_name) {
        updated.policy_name = extractedData.policy_name;
        filled.add("policy_name");
      }
      if (extractedData.policy_from) {
        updated.policy_from = extractedData.policy_from;
        filled.add("policy_from");
      }
      if (extractedData.policy_to) {
        updated.policy_to = extractedData.policy_to;
        filled.add("policy_to");
      }
      if (extractedData.sum_assured != null) {
        updated.sum_assured = String(extractedData.sum_assured);
        filled.add("sum_assured");
      }
      if (extractedData.premium_amount != null) {
        updated.premium_amount = String(extractedData.premium_amount);
        filled.add("premium_amount");
      }
      if (extractedData.tpa) {
        updated.tpa = extractedData.tpa;
        filled.add("tpa");
      }
      if (extractedData.nominee) {
        updated.nominee = extractedData.nominee;
        filled.add("nominee");
      }
      return updated;
    });

    if (extractedData.type_specific_data && Object.keys(extractedData.type_specific_data).length > 0) {
      setTypeSpecificData(extractedData.type_specific_data);
      filled.add("type_specific_data");
    }

    // Handle company matching
    if (extractedData.insurance_company) {
      const match = companies.find(
        (c) => c.name.toLowerCase() === extractedData.insurance_company!.toLowerCase()
      );
      if (match) {
        setIsOtherCompany(false);
        setCustomCompanyName("");
      } else {
        setIsOtherCompany(true);
        setCustomCompanyName(extractedData.insurance_company);
      }
    }

    setAutoFilledFields(filled);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extractedData]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setDocuments((prev) => [...prev, ...Array.from(e.target.files!)]);
    }
  };

  const removeFile = (index: number) => {
    setDocuments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDeleteExistingDocument = (documentId: number) => {
    if (!id) return;
    setConfirmDialog({
      open: true,
      title: t('insurance:form.deleteDocument'),
      message: t('insurance:form.deleteDocumentConfirm'),
      onConfirm: async () => {
        try {
          await insuranceService.deleteDocument(Number(id), documentId);
          setExistingDocuments((prev) => prev.filter((doc) => doc.id !== documentId));
        } catch (err) {
          console.error("Failed to delete document:", err);
          showToast(t('insurance:messages.deleteDocumentFailed'));
        }
      },
    });
  };

  // Floater member helpers
  const getRelName = (dep: DependantOption): string => {
    if (typeof dep.relationship === "string") return dep.relationship;
    if (typeof dep.relationship === "object" && dep.relationship !== null && "name" in dep.relationship) {
      return dep.relationship.name;
    }
    const rt = relationshipTypes.find((r) => r.id === dep.relationship);
    return rt?.name || "";
  };

  const addFloaterMember = (dep: DependantOption) => {
    if (floaterMembers.some((m) => m.dependant_id === dep.id)) return;
    setFloaterMembers((prev) => [
      ...prev,
      { dependant_id: dep.id, name: dep.name, relationship: getRelName(dep), uhid: "" },
    ]);
  };

  const removeFloaterMember = (dependantId: number) => {
    setFloaterMembers((prev) => prev.filter((m) => m.dependant_id !== dependantId));
  };

  const updateFloaterUhid = (dependantId: number, uhid: string) => {
    setFloaterMembers((prev) =>
      prev.map((m) => (m.dependant_id === dependantId ? { ...m, uhid } : m))
    );
  };

  const availableDependants = dependants.filter(
    (d) => !floaterMembers.some((m) => m.dependant_id === d.id)
  );

  // Add new member via API
  const handleNewMemberChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setNewMember((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddNewMember = async () => {
    if (!newMember.name.trim() || !newMember.relationship) return;
    setAddingMember(true);
    try {
      const res = await axiosInstance.post(API_ENDPOINTS.dependants.list, {
        name: newMember.name.trim(),
        relationship: Number(newMember.relationship),
        gender: newMember.gender || undefined,
        dob: newMember.dob || undefined,
        mobile_number: newMember.mobile_number || undefined,
        email: newMember.email || undefined,
      });
      const created = res.data.data || res.data;
      setDependants((prev) => [...prev, created]);
      addFloaterMember(created);
      setNewMember({ name: "", relationship: "", gender: "", dob: "", mobile_number: "", email: "" });
      setShowAddForm(false);
    } catch (err) {
      console.error("Failed to add member:", err);
      showToast(t('insurance:messages.addMemberFailed'));
    } finally {
      setAddingMember(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      form.policy_owner_type === "dependant" &&
      form.plan_type === "individual" &&
      !form.dependant
    ) {
      showToast("Please pick a family member for this policy.");
      return;
    }

    setSaving(true);

    const payload: InsurancePolicyPayload = {
      policy_owner_type: form.policy_owner_type,
      plan_type: form.plan_type,
      dependant:
        form.policy_owner_type === "dependant" && form.dependant
          ? form.dependant
          : undefined,
      policy_from: form.policy_from,
      policy_to: form.policy_to,
      type_of_insurance: form.type_of_insurance,
      insurance_company: form.insurance_company,
      policy_number: form.policy_number,
      policy_name: form.policy_name || undefined,
      sum_assured: form.sum_assured ? Number(form.sum_assured) : undefined,
      premium_amount: form.premium_amount ? Number(form.premium_amount) : undefined,
      tpa: form.tpa || undefined,
      nominee: form.nominee || undefined,
      notes: form.notes || undefined,
      group_policy_details:
        form.policy_owner_type === "company" && form.group_policy_details
          ? form.group_policy_details
          : undefined,
      policy_features: form.policy_features || undefined,
      type_specific_data: typeSpecificData,
      renewal_reminder_enabled: form.renewal_reminder_enabled,
      renewal_frequency: form.renewal_reminder_enabled ? form.renewal_frequency : undefined,
      renewal_reminder_type: form.renewal_reminder_enabled ? form.renewal_reminder_type : undefined,
      renewal_reminder_value: form.renewal_reminder_enabled ? Number(form.renewal_reminder_value) : undefined,
    };

    // Include floater data when plan type is floater
    if (form.plan_type === "floater") {
      payload.is_self_included = true;
      payload.floater_members = floaterMembers.map((m) => ({
        dependant_id: m.dependant_id,
        uhid: m.uhid || undefined,
      }));
    }

    try {
      if (isEdit) {
        await insuranceService.updatePolicy(Number(id), payload, documents.length > 0 ? documents : undefined);
      } else {
        await insuranceService.createPolicy(payload, documents.length > 0 ? documents : undefined);
      }
      navigate("/insurance-record");
    } catch (err) {
      console.error("Failed to save policy:", err);
      showToast(t('insurance:messages.savePolicyFailed'));
    } finally {
      setSaving(false);
    }
  };

  const aif = (field: string) => autoFilledFields.has(field) ? " irf-field-ai-filled" : "";

  const ownerTypes = choices?.policy_owner_type || { self: "Self", dependant: "Dependant", company: "Company" };
  const insuranceTypes = choices?.type_of_insurance || {
    health: "Health Insurance",
    life: "Life Insurance",
    motor: "Motor/Vehicle Insurance",
    personal_accident: "Personal Accident",
    critical_illness: "Critical Illness",
    travel: "Travel Insurance",
    home_property: "Home/Property Insurance",
    other: "Other",
  };

  return (
    <div className="irf-page">

      {/* ============ BANNER ============ */}
      <section className="irf-banner">
        <Container>
          <BackButton />
          <div className="irf-banner-layout">
            <div className="irf-banner-text">
              {!isEdit && (
                <div className="irf-banner-badge">
                  <FontAwesomeIcon icon={faShieldHalved} />
                  {t('insurance:form.newPolicy')}
                </div>
              )}
              <h1 className="irf-banner-title">
                <FontAwesomeIcon icon={faShieldHalved} />
                {isEdit ? t('insurance:form.updateInsuranceRecord') : t('insurance:form.addInsuranceRecord')}
              </h1>
              <p className="irf-banner-subtitle">
                {isEdit ? t('insurance:form.fillDetailsUpdate') : t('insurance:form.fillDetailsAdd')}
              </p>
            </div>
          </div>
        </Container>
      </section>

      {/* ============ FORM ============ */}
      <Container>
        <form className="irf-form-card" onSubmit={handleSubmit}>

          {/* ===== AI EXTRACTION SECTION ===== */}
          {(
            <div className="irf-section irf-extract-section">
              <h3
                className="irf-section-label irf-section-label-toggle"
                onClick={() => setScannerExpanded(prev => !prev)}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                <span>
                  <FontAwesomeIcon icon={faWandMagicSparkles} style={{ marginRight: 8 }} />
                  {t('insurance:form.smartDocumentScanner')}
                </span>
                <FontAwesomeIcon
                  icon={scannerExpanded ? faChevronUp : faChevronDown}
                  className="irf-toggle-icon"
                />
              </h3>

              {scannerExpanded && (
              <>
              <p className="irf-section-hint">
                {t('insurance:form.scannerHint')}
              </p>

              {extractionStep === "idle" && (
                <label className="irf-extract-upload">
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleExtractionUpload}
                    hidden
                  />
                  <FontAwesomeIcon icon={faRobot} className="irf-extract-icon" />
                  <span className="irf-extract-text">{t('insurance:form.uploadToAutoFill')}</span>
                  <span className="irf-extract-hint">{t('insurance:form.uploadHint')}</span>
                </label>
              )}

              {extractionStep === "scanning" && (
                <div className="irf-extract-progress">
                  <FontAwesomeIcon icon={faSpinner} spin className="irf-extract-spinner" />
                  <div className="irf-extract-progress-info">
                    <span className="irf-extract-status">{t('insurance:form.scanningDocument')}</span>
                    <div className="irf-extract-bar">
                      <div
                        className="irf-extract-bar-fill"
                        style={{ width: `${extractionProgress}%` }}
                      />
                    </div>
                    <span className="irf-extract-percent">{t('insurance:form.percentComplete', { percent: extractionProgress })}</span>
                  </div>
                </div>
              )}

              {extractionStep === "extracting" && (
                <div className="irf-extract-progress">
                  <FontAwesomeIcon icon={faSpinner} spin className="irf-extract-spinner" />
                  <div className="irf-extract-progress-info">
                    <span className="irf-extract-status">{t('insurance:form.aiReadingDocument')}</span>
                    <span className="irf-extract-percent">{t('insurance:form.mayTakeFewSeconds')}</span>
                  </div>
                </div>
              )}

              {extractionStep === "done" && (
                <div className="irf-extract-success">
                  <FontAwesomeIcon icon={faCheckCircle} className="irf-extract-success-icon" />
                  <div className="irf-extract-success-info">
                    <span className="irf-extract-success-title">{t('insurance:form.fieldsExtractedSuccess')}</span>
                    <span className="irf-extract-success-hint">
                      {t('insurance:form.autoFilledFields', { count: autoFilledFields.size })}
                    </span>
                  </div>
                  <button type="button" className="irf-extract-reset-btn" onClick={resetExtraction}>
                    {t('insurance:form.tryAnotherDocument')}
                  </button>
                </div>
              )}

              {extractionStep === "error" && (
                <div className="irf-extract-error">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="irf-extract-error-icon" />
                  <div className="irf-extract-error-info">
                    <span className="irf-extract-error-title">{t('insurance:form.extractionFailed')}</span>
                    <span className="irf-extract-error-hint">{extractionError}</span>
                  </div>
                  <button type="button" className="irf-extract-reset-btn" onClick={resetExtraction}>
                    {t('insurance:form.tryAgain')}
                  </button>
                </div>
              )}
              </>
              )}
            </div>
          )}

          {/* Section: Type of Insurance */}
          <div className="irf-section">
            <h3 className="irf-section-label">{t('insurance:form.typeOfInsurance')}</h3>
            <div className="irf-pill-group">
              {Object.entries(insuranceTypes).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  className={`irf-pill${form.type_of_insurance === key ? " active" : ""}`}
                  onClick={() => handleInsuranceTypeChange({ target: { value: key } } as React.ChangeEvent<HTMLSelectElement>)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Section: Policy Owner Type */}
          <div className="irf-section">
            <h3 className="irf-section-label">{t('insurance:form.policyOwner')}</h3>
            <div className="irf-pill-group">
              {Object.entries(ownerTypes)
                .filter(([key]) => key !== "company" || COMPANY_OWNER_TYPES.has(form.type_of_insurance))
                .map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    className={`irf-pill${form.policy_owner_type === key ? " active" : ""}`}
                    onClick={() => setForm((prev) => ({ ...prev, policy_owner_type: key as any }))}
                  >
                    {label}
                  </button>
                ))}
            </div>

            {form.policy_owner_type === "dependant" && form.plan_type === "individual" && (
              <div className="irf-field" style={{ marginTop: 12 }}>
                <label className="irf-label">Which family member? *</label>
                {loadingDependants ? (
                  <div className="irf-company-loading">
                    <FontAwesomeIcon icon={faSpinner} spin /> Loading family members...
                  </div>
                ) : dependants.length === 0 ? (
                  <p className="irf-section-hint">
                    No family members found. Add one from the Family Members screen first.
                  </p>
                ) : (
                  <select
                    className="irf-select"
                    value={form.dependant ?? ""}
                    onChange={(e) => setForm((prev) => ({
                      ...prev,
                      dependant: e.target.value ? Number(e.target.value) : null,
                    }))}
                    required
                  >
                    <option value="">Select a family member</option>
                    {dependants.map((d) => (
                      <option key={d.id} value={d.id}>{d.name}</option>
                    ))}
                  </select>
                )}
              </div>
            )}

            <p className="irf-section-hint" style={{ marginTop: 12 }}>
              Policy holder: <strong>{getPolicyHolderPreview(form, dependants, currentUserName)}</strong>
              <span style={{ opacity: 0.7 }}> (auto-filled based on owner)</span>
            </p>
          </div>

          {/* Section: Plan Type — only for floater-eligible insurance types */}
          {FLOATER_ELIGIBLE_TYPES.has(form.type_of_insurance) && (
            <div className="irf-section">
              <h3 className="irf-section-label">{t('insurance:form.planType')}</h3>
              <div className="irf-pill-group">
                <button
                  type="button"
                  className={`irf-pill${form.plan_type === "individual" ? " active" : ""}`}
                  onClick={() => setForm((prev) => ({ ...prev, plan_type: "individual" }))}
                >
                  {t('insurance:form.individual')}
                </button>
                <button
                  type="button"
                  className={`irf-pill${form.plan_type === "floater" ? " active" : ""}`}
                  onClick={() => setForm((prev) => ({ ...prev, plan_type: "floater" }))}
                >
                  Floater
                </button>
              </div>
            </div>
          )}

          {/* Section: Floater Members (only when plan_type is floater) */}
          {form.plan_type === "floater" && (
            <div className="irf-section">
              <h3 className="irf-section-label">
                <FontAwesomeIcon icon={faUsers} style={{ marginRight: 8 }} />
                Covered Members
              </h3>
              <p className="irf-section-hint">
                Add the family members covered under this floater policy.
              </p>

              {/* Added members list */}
              {floaterMembers.length > 0 && (
                <div className="irf-floater-list">
                  {floaterMembers.map((member) => (
                    <div className="irf-floater-card" key={member.dependant_id}>
                      <div className="irf-floater-card-info">
                        <span className="irf-floater-card-name">{member.name}</span>
                        {member.relationship && (
                          <span className="irf-floater-card-rel">{member.relationship}</span>
                        )}
                      </div>
                      <div className="irf-floater-card-actions">
                        <input
                          className="irf-floater-card-uhid"
                          placeholder="UHID (optional)"
                          value={member.uhid}
                          onChange={(e) => updateFloaterUhid(member.dependant_id, e.target.value)}
                        />
                        <button
                          type="button"
                          className="irf-floater-remove-btn"
                          onClick={() => removeFloaterMember(member.dependant_id)}
                          title="Remove member"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Add existing dependant dropdown */}
              {loadingDependants ? (
                <div className="irf-floater-loading">
                  <FontAwesomeIcon icon={faSpinner} spin /> Loading family members...
                </div>
              ) : availableDependants.length > 0 ? (
                <div className="irf-floater-add">
                  <FontAwesomeIcon icon={faUserPlus} className="irf-floater-add-icon" />
                  <select
                    className="irf-floater-add-select"
                    value=""
                    onChange={(e) => {
                      const dep = dependants.find((d) => d.id === Number(e.target.value));
                      if (dep) addFloaterMember(dep);
                    }}
                  >
                    <option value="" disabled>Add existing family member...</option>
                    {availableDependants.map((dep) => (
                      <option key={dep.id} value={dep.id}>
                        {dep.name}{getRelName(dep) ? ` (${getRelName(dep)})` : ""}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}

              {/* Inline Add New Member form */}
              {!showAddForm ? (
                <button
                  type="button"
                  className="irf-add-new-member-btn"
                  onClick={() => setShowAddForm(true)}
                >
                  <FontAwesomeIcon icon={faPlus} />
                  Add New Family Member
                </button>
              ) : (
                <div className="irf-new-member-form">
                  <div className="irf-new-member-header">
                    <h4 className="irf-new-member-title">
                      <FontAwesomeIcon icon={faUserPlus} />
                      New Family Member
                    </h4>
                    <button
                      type="button"
                      className="irf-new-member-close"
                      onClick={() => {
                        setShowAddForm(false);
                        setNewMember({ name: "", relationship: "", gender: "", dob: "", mobile_number: "", email: "" });
                      }}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>
                  <div className="irf-new-member-grid">
                    <div className="irf-field">
                      <label>Full Name *</label>
                      <input
                        name="name"
                        value={newMember.name}
                        onChange={handleNewMemberChange}
                        placeholder="Enter full name"
                      />
                    </div>
                    <div className="irf-field">
                      <label>Relationship *</label>
                      <select
                        name="relationship"
                        value={newMember.relationship}
                        onChange={handleNewMemberChange}
                      >
                        <option value="" disabled>Select relationship</option>
                        {relationshipTypes.map((rt) => (
                          <option key={rt.id} value={rt.id}>{rt.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="irf-field">
                      <label>Gender</label>
                      <select
                        name="gender"
                        value={newMember.gender}
                        onChange={handleNewMemberChange}
                      >
                        <option value="">Select gender</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div className="irf-field">
                      <label>Date of Birth</label>
                      <input
                        name="dob"
                        type="date"
                        value={newMember.dob}
                        onChange={handleNewMemberChange}
                      />
                    </div>
                    <div className="irf-field">
                      <label>Mobile Number</label>
                      <input
                        name="mobile_number"
                        value={newMember.mobile_number}
                        onChange={handleNewMemberChange}
                        placeholder="e.g. 9876543210"
                      />
                    </div>
                    <div className="irf-field">
                      <label>Email</label>
                      <input
                        name="email"
                        type="email"
                        value={newMember.email}
                        onChange={handleNewMemberChange}
                        placeholder="e.g. john@example.com"
                      />
                    </div>
                  </div>
                  <div className="irf-new-member-actions">
                    <button
                      type="button"
                      className="irf-submit-btn"
                      disabled={addingMember || !newMember.name.trim() || !newMember.relationship}
                      onClick={handleAddNewMember}
                    >
                      {addingMember ? (
                        <>
                          <FontAwesomeIcon icon={faSpinner} spin />
                          Adding...
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faUserPlus} />
                          Add Member
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      className="irf-cancel-btn"
                      onClick={() => {
                        setShowAddForm(false);
                        setNewMember({ name: "", relationship: "", gender: "", dob: "", mobile_number: "", email: "" });
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Section: Policy Details */}
          <div className="irf-section">
            <h3 className="irf-section-label">Policy Details</h3>
            <div className="irf-grid">
              <div className={`irf-field${aif("insurance_company")}`}>
                <label>Insurance Company *</label>
                {loadingCompanies ? (
                  <div className="irf-company-loading">
                    <FontAwesomeIcon icon={faSpinner} spin /> Loading companies...
                  </div>
                ) : (
                  <>
                    <select
                      value={isOtherCompany ? "__other__" : form.insurance_company}
                      onChange={handleCompanyChange}
                      required={!isOtherCompany}
                    >
                      <option value="">Select company</option>
                      {companies.map((c) => (
                        <option key={c.id} value={c.name}>{c.name}</option>
                      ))}
                      <option value="__other__">Other (type manually)</option>
                    </select>
                    {isOtherCompany && (
                      <input
                        className="irf-other-company-input"
                        value={customCompanyName}
                        onChange={handleCustomCompanyChange}
                        placeholder="Enter company name"
                        required
                      />
                    )}
                  </>
                )}
              </div>
              <div className={`irf-field${aif("policy_number")}`}>
                <label>Policy Number *</label>
                <input name="policy_number" value={form.policy_number} onChange={handleChange} placeholder="e.g. POL-12345678" required />
              </div>
              <div className={`irf-field${aif("policy_name")}`}>
                <label>Policy Name</label>
                <input name="policy_name" value={form.policy_name} onChange={handleChange} placeholder="e.g. Health Shield Gold" />
              </div>
              <div className={`irf-field${aif("policy_from")}`}>
                <label>Policy From *</label>
                <input name="policy_from" type="date" value={form.policy_from} onChange={handleChange} required />
              </div>
              <div className={`irf-field${aif("policy_to")}`}>
                <label>Policy To *</label>
                <input name="policy_to" type="date" value={form.policy_to} onChange={handleChange} required />
              </div>
            </div>
          </div>

          {/* Section: Type-Specific Fields */}
          <TypeSpecificSection
            insuranceType={form.type_of_insurance}
            data={typeSpecificData}
            onChange={setTypeSpecificData}
          />

          {/* Section: Financial Details */}
          <div className="irf-section">
            <h3 className="irf-section-label">Financial Details</h3>
            <div className="irf-grid">
              <div className={`irf-field${aif("sum_assured")}`}>
                <label>Sum Assured</label>
                <input name="sum_assured" type="number" value={form.sum_assured} onChange={handleChange} placeholder="e.g. 500000" />
              </div>
              <div className={`irf-field${aif("premium_amount")}`}>
                <label>Premium Amount</label>
                <input name="premium_amount" type="number" value={form.premium_amount} onChange={handleChange} placeholder="e.g. 12000" />
              </div>
              <div className={`irf-field${aif("tpa")}`}>
                <label>TPA (Third Party Administrator)</label>
                <input name="tpa" value={form.tpa} onChange={handleChange} placeholder="e.g. Medi Assist" />
              </div>
              <div className={`irf-field${aif("nominee")}`}>
                <label>Nominee</label>
                <input name="nominee" value={form.nominee} onChange={handleChange} placeholder="Full name of nominee" />
              </div>
            </div>
          </div>

          {/* Section: Documents */}
          <div className="irf-section">
            <h3 className="irf-section-label">Documents</h3>

            {/* Existing documents (edit mode) */}
            {isEdit && existingDocuments.length > 0 && (
              <div className="irf-existing-documents">
                <p className="irf-section-hint">Previously uploaded documents:</p>
                <div className="irf-file-list">
                  {existingDocuments.map((doc) => {
                    const fileUrl = doc.file.startsWith("http") ? doc.file : `${API_BASE_URL}${doc.file}`;
                    const fileName = doc.file_name || doc.file.split("/").pop() || `Document ${doc.id}`;
                    return (
                      <div className="irf-file-chip" key={doc.id}>
                        <a
                          href={fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ display: "inline-flex", alignItems: "center", gap: 6, textDecoration: "none", color: "inherit" }}
                        >
                          <FontAwesomeIcon icon={faDownload} />
                          <span>{fileName}</span>
                        </a>
                        <button type="button" onClick={() => handleDeleteExistingDocument(doc.id)} title="Delete document">
                          <FontAwesomeIcon icon={faTimesCircle} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <label className="irf-upload-area">
              <input type="file" multiple accept=".pdf,.jpg,.jpeg,.png" onChange={handleFileChange} hidden />
              <FontAwesomeIcon icon={faUpload} className="irf-upload-icon" />
              <span className="irf-upload-text">Click to upload policy documents</span>
              <span className="irf-upload-hint">PDF, JPG, PNG (max 10MB each)</span>
            </label>
            {documents.length > 0 && (
              <div className="irf-file-list">
                {documents.map((file, idx) => (
                  <div className="irf-file-chip" key={idx}>
                    <span>{file.name}</span>
                    <button type="button" onClick={() => removeFile(idx)}>
                      <FontAwesomeIcon icon={faTimesCircle} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Section: Renewal Reminder */}
          <div className="irf-section irf-renewal-section">
            <h3 className="irf-section-label">
              <FontAwesomeIcon icon={faBell} style={{ marginRight: 8 }} />
              Renewal Reminder
            </h3>
            <label className="irf-checkbox-row">
              <input
                type="checkbox"
                checked={form.renewal_reminder_enabled}
                onChange={(e) => setForm((prev) => ({ ...prev, renewal_reminder_enabled: e.target.checked }))}
              />
              <span className="irf-checkbox-label">
                Notify me before policy expires via email and WhatsApp
              </span>
            </label>

            {form.renewal_reminder_enabled && (
              <div className="irf-renewal-options">
                <div className="irf-row">
                  <div className="irf-field">
                    <label className="irf-label">Remind me</label>
                    <input
                      type="number"
                      min={1}
                      className="irf-input"
                      value={form.renewal_reminder_value}
                      onChange={(e) => setForm((prev) => ({ ...prev, renewal_reminder_value: e.target.value }))}
                      placeholder="e.g. 15"
                    />
                  </div>
                  <div className="irf-field">
                    <label className="irf-label">Unit</label>
                    <select
                      className="irf-select"
                      value={form.renewal_reminder_type}
                      onChange={(e) => setForm((prev) => ({ ...prev, renewal_reminder_type: e.target.value }))}
                    >
                      {Object.entries(choices?.renewal_reminder_type || {}).map(([value, label]) => (
                        <option key={value} value={value}>{String(label)}</option>
                      ))}
                    </select>
                  </div>
                  <div className="irf-field">
                    <label className="irf-label">Frequency</label>
                    <select
                      className="irf-select"
                      value={form.renewal_frequency}
                      onChange={(e) => setForm((prev) => ({ ...prev, renewal_frequency: e.target.value }))}
                    >
                      {Object.entries(choices?.renewal_frequency || {}).map(([value, label]) => (
                        <option key={value} value={value}>{String(label)}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <p className="irf-section-hint" style={{ marginTop: 8 }}>
                  Together this reads as "Remind me {form.renewal_reminder_value || 1} {form.renewal_reminder_type} before policy expiry."
                </p>
              </div>
            )}
          </div>

          {/* Section: Group Policy Details (company-owned policies only) */}
          {form.policy_owner_type === "company" && (
            <div className="irf-section">
              <h3 className="irf-section-label">Group Policy Details</h3>
              <textarea
                className="irf-textarea"
                name="group_policy_details"
                value={form.group_policy_details}
                onChange={handleChange}
                placeholder="Employer name, group ID, HR contact, coverage tier, etc."
                rows={3}
              />
            </div>
          )}

          {/* Section: Policy Features */}
          <div className="irf-section">
            <h3 className="irf-section-label">Policy Features</h3>
            <textarea
              className="irf-textarea"
              name="policy_features"
              value={form.policy_features}
              onChange={handleChange}
              placeholder="Key benefits, add-ons, or riders included in this policy..."
              rows={3}
            />
          </div>

          {/* Section: Notes */}
          <div className="irf-section">
            <h3 className="irf-section-label">Additional Notes</h3>
            <textarea
              className="irf-textarea"
              name="notes"
              value={form.notes}
              onChange={handleChange}
              placeholder="Any additional information about this policy..."
              rows={3}
            />
          </div>

          {/* Actions */}
          <div className="irf-actions">
            <button type="submit" className="irf-submit-btn" disabled={saving}>
              {saving ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin />
                  Saving...
                </>
              ) : (
                isEdit ? "Update Policy" : "Save Policy"
              )}
            </button>
            <button type="button" className="irf-cancel-btn" onClick={() => navigate("/insurance-record")}>
              Cancel
            </button>
          </div>
        </form>
      </Container>

      {/* In-app toast notification */}
      {toastMsg && (
        <div
          style={{
            position: 'fixed',
            bottom: 32,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10000,
            padding: '12px 24px',
            borderRadius: 12,
            color: '#fff',
            fontWeight: 500,
            fontSize: 14,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            boxShadow: '0 4px 24px rgba(0,0,0,0.18)',
            background: toastMsg.type === 'success' ? '#10b981'
              : toastMsg.type === 'warning' ? '#f59e0b'
              : '#ef4444',
          }}
        >
          <span>{toastMsg.message}</span>
          <button
            onClick={() => setToastMsg(null)}
            style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', marginLeft: 8, fontSize: 14 }}
          >
            ✕
          </button>
        </div>
      )}

      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onClose={() => setConfirmDialog(d => ({ ...d, open: false }))}
        confirmLabel="Delete"
        variant="danger"
      />
    </div>
  );
};

export default InsuranceRecord;
